import React from 'react'
import { useNavigate } from 'react-router-dom'
import { parseISO, format } from 'date-fns'
import { services } from '../../services'
import { ROUTES } from '../../constants/routes'
import { getHomeAwayStatus } from '../../utils/match'
import './NextMatchHeroCard.css'

/**
 * NextMatchHeroCard - Hero card component that displays the user's next scheduled match
 * Prominently shows opponent, date/time, location, and primary action
 *
 * Props:
 * - match: The next match object
 * - loading: Boolean indicating if data is loading
 * - error: Error message if data fetch failed
 * - onScheduleMatch: Callback when user wants to schedule a match (empty state)
 * - onCancelMatch: Callback when user wants to cancel the match
 */
export default function NextMatchHeroCard({
  match,
  loading,
  error,
  onScheduleMatch,
  onCancelMatch,
}) {
  const navigate = useNavigate()

  const getOpponentName = (m) => {
    const viewerId = m?.current_player_id
    try {
      return services.match.getOpponentName(m, viewerId) || 'Opponent'
    } catch {
      return 'Opponent'
    }
  }

  const getHomeCourtInfo = (m) => {
    if (!m?.home_court_name) return null
    const courtName = m.home_court_name
    const location = [m.home_court_city, m.home_court_state]
      .filter(Boolean)
      .join(', ')
    return { courtName, location }
  }

  const formatDateTime = (datetime) => {
    if (!datetime) return 'TBD'
    try {
      const dateObj = parseISO(datetime)
      return format(dateObj, 'EEEE, MMMM d, yyyy • h:mm a')
    } catch {
      return String(datetime)
    }
  }

  const handleEnterScore = (e) => {
    e.stopPropagation()
    if (match) {
      navigate(ROUTES.matchScore(match.league_id, match.id))
    }
  }

  const handleCancelMatch = (e) => {
    e.stopPropagation()
    if (match && onCancelMatch) {
      onCancelMatch(match)
    }
  }

  const handleViewAllMatches = () => {
    navigate(ROUTES.SCHEDULED_MATCHES)
  }

  if (loading) {
    return (
      <div className="next-match-hero loading" role="status" aria-live="polite">
        <div className="skeleton-content">
          <div className="skeleton-badge" aria-hidden="true" />
          <div className="skeleton-title" aria-hidden="true" />
          <div className="skeleton-details" aria-hidden="true">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
          <div className="skeleton-actions" aria-hidden="true">
            <div className="skeleton-button" />
            <div className="skeleton-button" />
          </div>
          <span className="sr-only">Loading your next match...</span>
        </div>
      </div>
    )
  }

  // Error state - failed to load match data
  if (error) {
    return (
      <div className="next-match-hero empty-state" role="alert">
        <div className="hero-content">
          <div className="empty-icon" aria-hidden="true">
            ⚠️
          </div>
          <h2 className="hero-title">Unable to Load Match</h2>
          <p className="hero-subtitle">
            We couldn&apos;t load your next match. Please try again later.
          </p>
          <div className="hero-actions">
            <button
              className="hero-btn primary"
              onClick={onScheduleMatch}
              data-track="navigation.view_leagues_from_error"
            >
              <span aria-hidden="true">🎾</span> View My Leagues
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Empty state - no scheduled matches: render nothing
  if (!match) {
    return null
  }

  // Match exists - show the next match
  const opponentName = getOpponentName(match)
  const homeCourt = getHomeCourtInfo(match)

  // Determine home/away status
  const viewerId = match?.current_player_id
  const homeAwayStatus = getHomeAwayStatus(match, viewerId)
  const isViewerHome = homeAwayStatus.isHome
  const homeAwayText = homeAwayStatus.text.toUpperCase()
  const homeAwayIcon = homeAwayStatus.icon

  return (
    <div className="next-match-hero">
      <div className="hero-badge">Next Match</div>
      <div className="hero-content">
        <div className="hero-main">
          <h2 className="hero-title">
            {homeAwayIcon && (
              <span
                className="home-away-badge-inline"
                aria-label={isViewerHome ? 'Home match' : 'Away match'}
                title={
                  isViewerHome
                    ? 'You host this match'
                    : 'You travel to their court'
                }
              >
                {homeAwayIcon}
              </span>
            )}
            {homeAwayText && <span className="vs-label">{homeAwayText}</span>}
            <span className="vs-label">vs</span> {opponentName}
          </h2>

          <div className="hero-details">
            <div className="detail-item">
              <span className="detail-icon" aria-hidden="true">
                📅
              </span>
              <span className="detail-text">
                {formatDateTime(match.match_datetime)}
              </span>
            </div>

            {homeCourt && (
              <>
                <div className="detail-item">
                  <span className="detail-icon" aria-hidden="true">
                    🎾
                  </span>
                  <span className="detail-text">{homeCourt.courtName}</span>
                </div>
                {homeCourt.location && (
                  <div className="detail-item">
                    <span className="detail-icon" aria-hidden="true">
                      📍
                    </span>
                    <span className="detail-text">{homeCourt.location}</span>
                  </div>
                )}
              </>
            )}

            <div className="detail-item league-info">
              <span className="detail-icon" aria-hidden="true">
                🏆
              </span>
              <span className="detail-text">
                {match.league_name} • {match.division_name}
              </span>
            </div>
          </div>
        </div>

        <div className="hero-actions">
          <button
            className="hero-btn primary"
            onClick={handleEnterScore}
            data-track="match.enter_score_from_hero"
          >
            <span aria-hidden="true">✏️</span> Enter Score
          </button>
          <button
            className="hero-btn secondary"
            onClick={handleCancelMatch}
            data-track="match.cancel_from_hero"
          >
            <span aria-hidden="true">🗑️</span> Cancel
          </button>
        </div>

        <button
          className="view-all-link"
          onClick={handleViewAllMatches}
          data-track="navigation.view_all_matches_from_hero"
        >
          View all scheduled matches →
        </button>
      </div>
    </div>
  )
}
